import React, { useState } from 'react';
import { Handshake, TrendingUp, HeadphonesIcon, Truck, BarChart3, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { dealerBenefits } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DealerSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    city: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Submit to backend API
    axios.post(`${BACKEND_URL}/api/dealer-enquiry`, formData)
      .then(response => {
        console.log('Dealer Enquiry Submitted:', response.data);
        toast.success('Enquiry Submitted Successfully!', {
          description: 'Our team will contact you within 24 hours.'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          city: '',
          message: ''
        });
      })
      .catch(error => {
        console.error('Error submitting dealer enquiry:', error);
        toast.error('Failed to submit enquiry. Please try again.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <section id="dealer" className="py-20 bg-zinc-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(234,88,12,0.1),transparent_70%)]"></div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-orange-500 text-sm font-bold tracking-wider uppercase">Partnership Opportunity</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Partner With <span className="text-orange-500">RK ROTOARC</span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Join our growing network of successful dealers and distributors across India
          </p>
          <div className="w-24 h-1 bg-orange-600 mx-auto mt-6"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Benefits */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-6">Why Become Our Dealer?</h3>
            
            <div className="space-y-4 mb-8">
              {dealerBenefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-4 bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-lg hover:border-orange-600/30 transition-colors"
                >
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span className="text-zinc-300">{benefit}</span>
                </div>
              ))}
            </div>

            {/* Feature Cards */}
            <div className="grid grid-cols-2 gap-4 mt-8">
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4 text-center">
                  <TrendingUp className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">High Demand</div>
                  <div className="text-xs text-zinc-400 mt-1">Growing Market</div>
                </CardContent>
              </Card>
              
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4 text-center">
                  <HeadphonesIcon className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">24/7 Support</div>
                  <div className="text-xs text-zinc-400 mt-1">Dealer Assistance</div>
                </CardContent>
              </Card>
              
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4 text-center">
                  <Truck className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">Fast Delivery</div>
                  <div className="text-xs text-zinc-400 mt-1">Quick Supply</div>
                </CardContent>
              </Card>
              
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4 text-center">
                  <BarChart3 className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">Good Margins</div>
                  <div className="text-xs text-zinc-400 mt-1">Profitable Deals</div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right: Enquiry Form */}
          <div>
            <Card className="bg-zinc-900/80 backdrop-blur-sm border-zinc-800">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-orange-600 flex items-center justify-center">
                    <Handshake className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Dealer Enquiry Form</h3>
                    <p className="text-zinc-400 text-sm">Fill in your details to get started</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-zinc-300">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="mt-1.5 bg-zinc-800 border-zinc-700 text-white focus:border-orange-600"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-zinc-300">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-1.5 bg-zinc-800 border-zinc-700 text-white focus:border-orange-600"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-zinc-300">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="mt-1.5 bg-zinc-800 border-zinc-700 text-white focus:border-orange-600"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>

                  <div>
                    <Label htmlFor="company" className="text-zinc-300">Company Name</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="mt-1.5 bg-zinc-800 border-zinc-700 text-white focus:border-orange-600"
                      placeholder="Your company name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="city" className="text-zinc-300">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="mt-1.5 bg-zinc-800 border-zinc-700 text-white focus:border-orange-600"
                      placeholder="Your city"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-zinc-300">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="mt-1.5 bg-zinc-800 border-zinc-700 text-white focus:border-orange-600"
                      placeholder="Tell us about your business and requirements..."
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-6 text-lg"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
                  </Button>

                  <p className="text-zinc-500 text-xs text-center">
                    We'll get back to you within 24 hours
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DealerSection;